import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useOptionalAdminTheme } from '@shared/providers/admin-theme/AdminThemeContext';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2, SERVICE_TYPE_BOTH } from '@/constants/serviceType';
import { getSiteTheme } from './configs';
import type {
  ColorPalette,
  ServiceThemeConfig,
  SiteThemeConfig,
} from './types';
import { type ServiceType, type SiteType } from './types';

const ADMIN_ROUTE_SEGMENT = '/admin';

const DEFAULT_SERVICE_SELECTOR = {
  backgroundColor: '#1f2937',
  buttonActiveOpacity: 'ff',
  buttonInactiveOpacity: '40',
} as const;

/** Admin theme slice (from AdminThemeContext) → ServiceThemeConfig for ThemeContext consumers */
function adminThemeToServiceConfig(theme: {
  name?: string;
  displayName?: string;
  colors: Record<string, string>;
  logoPath?: string;
  sidebarWidth?: string;
}): ServiceThemeConfig {
  return {
    name: theme.name ?? 'Admin',
    displayName: theme.displayName ?? 'Admin',
    colors: theme.colors as unknown as ColorPalette,
    logoPath: theme.logoPath ?? '',
    sidebarWidth: theme.sidebarWidth ?? 'w-60',
  };
}

/** Build synthetic SiteThemeConfig from admin theme (so clinic code paths still work on admin route) */
function buildSiteConfigFromAdmin(adminTheme: NonNullable<ReturnType<typeof useOptionalAdminTheme>>): SiteThemeConfig {
  const serviceTheme = adminThemeToServiceConfig(adminTheme.theme);
  return {
    siteType: adminTheme.siteType ?? 'medix',
    siteName: adminTheme.themeConfig.siteName ?? 'Admin',
    A2: serviceTheme,
    R2: serviceTheme,
    serviceSelector: { ...DEFAULT_SERVICE_SELECTOR },
  };
}

/**
 * Clinic Service Type restriction
 * - 'r2': Only Massage (R2)
 * - 'a2': Only Acupuncture (A2)
 * - 'both' | null: Both available (null = default)
 */
export type ClinicServiceType = 'r2' | 'a2' | 'both' | null;

export interface ThemeContextType {
  siteType: SiteType;
  serviceType: ServiceType;
  clinicServiceType: ClinicServiceType;
  canSwitchServiceType: boolean;
  themeConfig: SiteThemeConfig;
  theme: ServiceThemeConfig;
  colors: ColorPalette;
  setServiceType: (type: ServiceType) => void;
  toggleServiceType: () => void;
  setClinicServiceType: (type: ClinicServiceType) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export interface ThemeProviderProps {
  children: ReactNode;
  defaultServiceType?: ServiceType;
  siteType?: SiteType;
  clinicServiceType?: ClinicServiceType;
}

function resolveServiceTypeFromClinic(
  clinicServiceType: ClinicServiceType,
  defaultServiceType: ServiceType
): ServiceType {
  if (clinicServiceType?.toUpperCase() === SERVICE_TYPE_A2) return SERVICE_TYPE_A2;
  if (clinicServiceType?.toUpperCase() === SERVICE_TYPE_R2) return SERVICE_TYPE_R2;
  const stored = localStorage.getItem('service_type');
  if (stored === SERVICE_TYPE_R2 || stored === SERVICE_TYPE_A2) return stored as ServiceType;
  return defaultServiceType;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultServiceType = SERVICE_TYPE_A2,
  siteType: propSiteType,
  clinicServiceType: propClinicServiceType,
}) => {
  const { tenant, siteType: urlSiteType, site } = useParams<{
    tenant?: string;
    siteType?: string;
    site?: string;
  }>();
  const pathname = useLocation().pathname;
  const adminTheme = useOptionalAdminTheme();

  const siteType: SiteType = propSiteType || urlSiteType || site || tenant || 'medix';
  const isAdminRoute = pathname.includes(ADMIN_ROUTE_SEGMENT);

  const [clinicServiceType, setClinicServiceTypeState] = useState<ClinicServiceType>(
    propClinicServiceType ?? null
  );
  const [serviceType, setServiceTypeState] = useState<ServiceType>(() => {
    if (isAdminRoute && adminTheme) return 'adminTheme';
    return resolveServiceTypeFromClinic(clinicServiceType, defaultServiceType);
  });

  const canSwitchServiceType = useMemo(() => {
    if (isAdminRoute) return false;
    return clinicServiceType === null || clinicServiceType?.toUpperCase() === SERVICE_TYPE_BOTH;
  }, [clinicServiceType, isAdminRoute]);

  useEffect(() => {
    if (isAdminRoute && adminTheme) {
      setServiceTypeState('adminTheme');
      return;
    }
    if (clinicServiceType?.toUpperCase() === SERVICE_TYPE_A2) {
      setServiceTypeState(SERVICE_TYPE_A2);
      localStorage.setItem('service_type', SERVICE_TYPE_A2);
    } else if (clinicServiceType?.toUpperCase() === SERVICE_TYPE_R2) {
      setServiceTypeState(SERVICE_TYPE_R2);
      localStorage.setItem('service_type', SERVICE_TYPE_R2);
    } else {
      const next = resolveServiceTypeFromClinic(clinicServiceType, defaultServiceType);
      setServiceTypeState(next);
      if (next === SERVICE_TYPE_A2 || next === SERVICE_TYPE_R2) localStorage.setItem('service_type', next);
    }
  }, [isAdminRoute, adminTheme, clinicServiceType, defaultServiceType]);

  useEffect(() => {
    if (isAdminRoute && adminTheme) return;
    const restricted =
      clinicServiceType?.toUpperCase() === SERVICE_TYPE_A2 ||
      clinicServiceType?.toUpperCase() === SERVICE_TYPE_R2;
    if (restricted) return;

    const onStorage = (e: StorageEvent) => {
      if (e.key !== 'service_type' || !e.newValue) return;
      if (e.newValue === SERVICE_TYPE_A2 || e.newValue === SERVICE_TYPE_R2) {
        setServiceTypeState(e.newValue as ServiceType);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [isAdminRoute, adminTheme, clinicServiceType]);

  const clinicThemeConfig = useMemo(() => getSiteTheme(siteType), [siteType]);
  const adminThemeConfigSynth = useMemo(
    () => (adminTheme ? buildSiteConfigFromAdmin(adminTheme) : null),
    [adminTheme]
  );
  const themeConfig = isAdminRoute && adminThemeConfigSynth
    ? adminThemeConfigSynth
    : clinicThemeConfig;

  const theme = useMemo(() => {
    if (serviceType === 'adminTheme' && adminTheme)
      return adminThemeToServiceConfig(adminTheme.theme);
    return serviceType === SERVICE_TYPE_A2 ? themeConfig.A2 : themeConfig.R2;
  }, [themeConfig, serviceType, adminTheme]);

  const colors = theme.colors;

  const setServiceType = useCallback(
    (type: ServiceType) => {
      if (serviceType === 'adminTheme') return;
      if (!canSwitchServiceType) {
        console.warn('Cannot change service type: clinic is restricted to', clinicServiceType);
        return;
      }
      setServiceTypeState(type);
      localStorage.setItem('service_type', type);
    },
    [canSwitchServiceType, clinicServiceType, serviceType]
  );

  const toggleServiceType = useCallback(() => {
    if (serviceType === 'adminTheme') return;
    if (!canSwitchServiceType) {
      console.warn('Cannot toggle service type: clinic is restricted to', clinicServiceType);
      return;
    }
    setServiceTypeState((prev) => {
      const next = prev === SERVICE_TYPE_A2 ? SERVICE_TYPE_R2 : SERVICE_TYPE_A2;
      localStorage.setItem('service_type', next);
      return next;
    });
  }, [canSwitchServiceType, clinicServiceType, serviceType]);

  const setClinicServiceType = useCallback((type: ClinicServiceType) => {
    setClinicServiceTypeState(type);
  }, []);

  const value = useMemo<ThemeContextType>(
    () => ({
      siteType,
      serviceType,
      clinicServiceType,
      canSwitchServiceType,
      themeConfig,
      theme,
      colors,
      setServiceType,
      toggleServiceType,
      setClinicServiceType,
    }),
    [
      siteType,
      serviceType,
      clinicServiceType,
      canSwitchServiceType,
      themeConfig,
      theme,
      colors,
      setServiceType,
      toggleServiceType,
      setClinicServiceType,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export function useTheme(): ThemeContextType {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider');
  return ctx;
}

/** Use theme when inside ThemeProvider; returns null otherwise (e.g. in Storybook). */
export function useOptionalTheme(): ThemeContextType | null {
  return useContext(ThemeContext);
}

export function useThemeConfig(
  siteType: SiteType,
  serviceType: ServiceType
): ServiceThemeConfig {
  return useMemo(() => {
    const siteConfig = getSiteTheme(siteType);
    return serviceType === SERVICE_TYPE_A2 ? siteConfig.A2 : siteConfig.R2;
  }, [siteType, serviceType]);
}

export type { ColorPalette, ServiceThemeConfig, SiteThemeConfig, ServiceType, SiteType };
